import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { getColorClasses } from '../config/series'
import { seriesConfig } from '../config/series.generated'
import {
  LEARN_LIBRARY_ONE_TIME_GBP,
  LEARN_SUBSCRIPTION_HIGHLIGHT_TITLE,
  LEARN_SUBSCRIPTION_PRICE_GBP_YEAR,
} from '../config/subscription'
import { useLocalProgress } from '../hooks/useLocalProgress'
import { MDXWrapper } from '../utils/mdx'
import { InteractivePythonBlock } from '../components/ui/InteractivePythonBlock'
import PythonGuideModal from '../components/ui/PythonGuideModal'
import { ModuleUpdateBanner } from '../components/ModuleUpdateBanner'
import SEOHead from '../components/seo/SEOHead'
import StructuredData from '../components/seo/StructuredData'
import { Lock, Gift, Clock, CheckCircle, KeyRound } from 'lucide-react'

// Dynamic MDX content loading
const loadMDXContent = async (seriesSlug: string, manualSlug: string) => {
  try {
    // Dynamically import the MDX file based on series and manual slug
    const module = await import(`../content/${seriesSlug}/${manualSlug}.mdx`)
    return module
  } catch (error) {
    console.error(`Failed to load MDX content for ${seriesSlug}/${manualSlug}:`, error)
    
    // Return error content if the specific MDX file doesn't exist
    return {
      default: () => (
        <div className="prose prose-lg max-w-none dark:prose-invert">
          <h1>Error Loading Manual Content</h1>
          <p className="text-lg text-red-600 dark:text-red-400">
            There was an error loading this manual's content. Please try refreshing the page.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Debug info: {seriesSlug}/{manualSlug}
          </p>
        </div>
      )
    }
  }
}

// Helper function to find manual by slug across all series
const findManualBySlug = (manualSlug: string) => {
  for (const series of seriesConfig) {
    const manual = series.manuals.find(m => m.slug === manualSlug);
    if (manual) {
      return { series, manual };
    }
  }
  return null;
};

// Check if module is free
// Bundle configuration: modules included with other purchases
// Note: Julia whitepaper is now embedded directly in create-cryptocurrency module
const BUNDLE_INCLUSIONS: Record<string, string[]> = {
  // Future bundle configurations can be added here
};

const isModuleFree = (manual: any): boolean => {
  return manual.free === true || manual.price === 0;
};

// Paywall Component for paid modules
function PaywallOverlay({ manual, series }: { manual: any; series: any }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-800 via-navy-900 to-navy-800">
      <header className="bg-navy-900/50 border-b border-navy-700">
        <div className="max-w-4xl mx-auto container-padding py-6">
          <nav className="mb-4">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link to="/" className="text-gold-400 hover:underline">Home</Link>
              </li>
              <li className="text-gray-400">→</li>
              <li>
                <Link to={`/series/${series.slug}`} className="text-gold-400 hover:underline">
                  {series.title}
                </Link>
              </li>
              <li className="text-gray-400">→</li>
              <li className="text-white font-medium">{manual.title}</li>
            </ol>
          </nav>
          <h1 className="text-2xl font-serif font-bold text-white">{manual.title}</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto container-padding py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-navy-800 rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-navy-800 to-navy-900 p-8 text-center">
            <div className="w-20 h-20 bg-gold-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-gold-400" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-white mb-2">
              Premium minimanual
            </h2>
            <p className="text-gray-300">
              This minimanual is part of the full library. Subscribe annually or buy the entire catalogue in one payment
              — we do not sell single modules.
            </p>
          </div>

          <div className="p-8">
            <div className="mb-8">
              <h3 className="font-serif text-xl font-bold text-navy-800 dark:text-white mb-3">
                {manual.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {manual.summary}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4 mr-1" />
                  {manual.duration}
                </div>
                {manual.difficulty && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                    manual.difficulty === 'beginner' ? 'bg-green-100 text-green-800' :
                    manual.difficulty === 'intermediate' ? 'bg-gold-100 text-gold-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {manual.difficulty}
                  </span>
                )}
              </div>
            </div>

            <div className="mb-8 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <h4 className="font-semibold text-navy-800 dark:text-white mb-2">Learn Library includes</h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                Every paid minimanual on this site for 12 months in your browser — including{' '}
                <strong className="text-navy-900 dark:text-white">{LEARN_SUBSCRIPTION_HIGHLIGHT_TITLE}</strong>, our
                flagship blockchain build walkthrough. Subscription is online reading only (no download bundle).
              </p>
              <Link
                to="/subscribe"
                className="inline-flex items-center justify-center w-full sm:w-auto bg-gold-600 hover:bg-gold-500 text-navy-900 font-bold px-8 py-4 rounded-lg transition-all"
              >
                Learn Library — £{LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}/year
              </Link>
            </div>

            <div className="mb-8 p-4 bg-gray-50 dark:bg-navy-700/50 rounded-lg border border-navy-600/30">
              <div className="flex items-start gap-3 mb-3">
                <KeyRound className="w-6 h-6 text-gold-500 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-navy-800 dark:text-white mb-1">Full library — one-time</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Unlock <strong>every</strong> minimanual (including this one) with a single payment of{' '}
                    <strong>£{LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)}</strong>. Pay on our secure Stripe page, then sign in
                    to My Library with the same email.
                  </p>
                  <Link
                    to="/subscribe"
                    className="inline-flex items-center justify-center bg-navy-800 hover:bg-navy-900 text-white px-6 py-3 rounded-lg font-semibold transition-all w-full sm:w-auto"
                  >
                    Go to checkout options
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                    Stripe Payment Link or subscription — both on the next page.{' '}
                    <Link to="/refund-policy" className="text-gold-600 hover:underline">
                      Refund policy
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-navy-600 text-center">
              <Link
                to="/#free-modules"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-medium text-sm"
              >
                <Gift className="w-4 h-4 mr-2" />
                Try our free sample modules first
              </Link>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default function ManualPage() {
  const [mdxContent, setMdxContent] = useState<React.ComponentType | null>(null)
  const [loading, setLoading] = useState(true)
  const { getProgressPercentage } = useLocalProgress()
  const [hasPython, setHasPython] = useState(false)
  const [showGuide, setShowGuide] = useState(false)
  const [entitlementsReady, setEntitlementsReady] = useState(false)
  const [sessionSlugSet, setSessionSlugSet] = useState<Set<string> | null>(null)
  const [jwtPayload, setJwtPayload] = useState<{
    purchasedProducts?: string[]
    learnSubscription?: boolean
    learnSubscriptionPeriodEnd?: number
  } | null>(null)

  // Extract manual slug from URL: support /seriesSlug/manualSlug or /manualSlug
  const { seriesSlug: seriesSlugParam, manualSlug: manualSlugParam } = useParams<{ seriesSlug?: string; manualSlug?: string }>();
  const manualSlug = manualSlugParam ?? (() => { const p = window.location.pathname.replace(/^\/+/, ''); return p.includes('/') ? p.split('/').pop() ?? p : p; })();

  useEffect(() => {
    let cancelled = false
    async function run() {
      const urlParams = new URLSearchParams(window.location.search)
      const urlToken = urlParams.get('token')
      if (urlToken) {
        localStorage.setItem('rondanini_jwt_token', urlToken)
        window.history.replaceState({}, '', window.location.pathname)
      }

      const sessionToken = localStorage.getItem('sessionToken')
      if (sessionToken) {
        try {
          const res = await fetch(
            `/.netlify/functions/user-dashboard?token=${encodeURIComponent(sessionToken)}`
          )
          const data = await res.json()
          if (!cancelled && data.success && Array.isArray(data.purchases)) {
            setSessionSlugSet(new Set(data.purchases.map((p: { slug: string }) => p.slug)))
          } else if (!cancelled) {
            setSessionSlugSet(new Set())
          }
        } catch {
          if (!cancelled) setSessionSlugSet(new Set())
        }
        if (!cancelled) setEntitlementsReady(true)
        return
      }

      const token = localStorage.getItem('rondanini_jwt_token')
      if (token) {
        try {
          const parts = token.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
            const now = Math.floor(Date.now() / 1000)
            if (payload.exp && payload.exp > now) {
              if (!cancelled) setJwtPayload(payload)
            }
          }
        } catch (e) {
          console.error('JWT decode error:', e)
        }
      }
      if (!cancelled) setEntitlementsReady(true)
    }
    setEntitlementsReady(false)
    setSessionSlugSet(null)
    setJwtPayload(null)
    run()
    return () => {
      cancelled = true
    }
  }, [manualSlug])

  // Find the manual and its series
  const result = manualSlug ? findManualBySlug(manualSlug) : null;
  const series = result?.series;
  const manual = result?.manual;

  const isFree = manual ? isModuleFree(manual) : false;

  const isIncludedWithBundle = false;
  const hasBundleAccess = false;

  const isPdfExport =
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    new URLSearchParams(window.location.search).get('pdf_export') === '1';

  const isLocalPreview =
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    new URLSearchParams(window.location.search).get('preview') === '1';

  const authLoading =
    !!manual &&
    !isFree &&
    !isPdfExport &&
    !isLocalPreview &&
    !entitlementsReady;

  const canAccess =
    !!manual &&
    (isFree ||
      isPdfExport ||
      isLocalPreview ||
      hasBundleAccess ||
      (entitlementsReady &&
        (() => {
          if (sessionSlugSet !== null) {
            const learn = sessionSlugSet.has('learn-library')
            const owns = sessionSlugSet.has(manual.slug)
            return owns || learn
          }
          if (jwtPayload) {
            const products = jwtPayload.purchasedProducts || []
            const owns = products.includes(manual.slug)
            const now = Math.floor(Date.now() / 1000)
            const subEnd = jwtPayload.learnSubscriptionPeriodEnd
            const subActive =
              jwtPayload.learnSubscription === true &&
              (subEnd == null || subEnd > now)
            return owns || subActive
          }
          return false
        })()));

  const showSubscriptionOnlineOnlyBanner =
    canAccess &&
    !isFree &&
    manual &&
    ((sessionSlugSet !== null &&
      sessionSlugSet.has('learn-library') &&
      !sessionSlugSet.has(manual.slug)) ||
      (jwtPayload &&
        jwtPayload.learnSubscription === true &&
        !(jwtPayload.purchasedProducts || []).includes(manual.slug) &&
        (jwtPayload.learnSubscriptionPeriodEnd == null ||
          jwtPayload.learnSubscriptionPeriodEnd > Math.floor(Date.now() / 1000))));

  useEffect(() => {
    if (series && manual && canAccess && !authLoading) {
      loadMDXContent(series.slug, manual.slug)
        .then((module) => {
          setMdxContent(() => module.default)
          setLoading(false)
        })
        .catch((error) => {
          console.error('Failed to load manual content:', error)
          setLoading(false)
        })
    } else if (!canAccess || authLoading) {
      setLoading(false)
    }
  }, [series, manual, canAccess, authLoading])

  // Detect if this manual contains Python blocks (from MDX render events)
  useEffect(() => {
    const handler = () => setHasPython(true)
    setHasPython(false)
    document.addEventListener('module-has-python' as any, handler as any)
    return () => {
      document.removeEventListener('module-has-python' as any, handler as any)
    }
  }, [manualSlug])

  if (!manualSlug) {
    return <div>Manual not found</div>
  }

  if (!series || !manual) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Manual not found: {manualSlug}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Available manuals: {seriesConfig.flatMap(s => s.manuals.map(m => m.slug)).join(', ')}
          </p>
          <Link to="/" className="bg-blue-600 text-white px-4 py-2 rounded">
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  const courseSeo = (
    <>
      <SEOHead
        title={manual.title}
        description={manual.summary || `${manual.title} — professional treasury and financial markets learning from Rondanini Publishing Ltd.`}
        canonicalUrl={`https://learn.rondanini.com/${manual.slug}`}
        ogType="article"
        keywords={manual.tags}
        category={series.title}
        tags={manual.tags}
      />
      <StructuredData type="course" manual={manual} series={series} />
      <StructuredData
        type="breadcrumb"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: series.title, url: `/series/${series.slug}` },
          { name: manual.title, url: `/${manual.slug}` },
        ]}
      />
    </>
  )

  if (authLoading) {
    return (
      <>
      {courseSeo}
      <div className="min-h-screen flex items-center justify-center bg-navy-900">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500 mx-auto mb-4" />
          <p className="text-gray-400">Checking your access…</p>
        </div>
      </div>
      </>
    );
  }

  if (!canAccess) {
    return (
      <>
        {courseSeo}
        <PaywallOverlay manual={manual} series={series} />
      </>
    );
  }

  const colorClasses = getColorClasses(series.color ?? 'blue')
  const progress = getProgressPercentage(manual.slug)

  if (loading) {
    return (
      <>
      {courseSeo}
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading manual...</p>
        </div>
      </div>
      </>
    )
  }

  return (
    <div className="min-h-screen">
      {courseSeo}
      {/* Free Module Banner */}
      {isFree && (
        <div className="bg-green-600 text-white text-center py-2 text-sm">
          <Gift className="w-4 h-4 inline mr-2" />
          <strong>Free Sample Module:</strong> Enjoy this complimentary content with lifetime access!
        </div>
      )}
      {/* Local preview bypass banner (localhost + ?preview=1) */}
      {isLocalPreview && (
        <div className="bg-amber-600 text-white text-center py-2 text-sm">
          <Lock className="w-4 h-4 inline mr-2" />
          <strong>Preview mode:</strong> Viewing paid content (localhost only). Use <code className="bg-amber-700 px-1 rounded">?preview=1</code> on any module URL to test.
        </div>
      )}
      {showSubscriptionOnlineOnlyBanner && (
        <div className="bg-blue-900 text-blue-100 text-center py-2 text-sm border-b border-blue-700">
          <strong>Learn Library subscription:</strong> you have online access only. Downloadable HTML and similar offline bundles are not included; purchase lifetime access to a module if you need a separate licensed download where offered.
        </div>
      )}

      {/* Module Update Banner */}
      {manual && <ModuleUpdateBanner moduleSlug={manual.slug} />}

      {/* Header */}
      <header className={`${colorClasses.bg} ${colorClasses.border} border-b`}>
        <div className="max-w-4xl mx-auto container-padding py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center space-x-2 text-sm">
                <li>
                  <Link to="/" className={`${colorClasses.accent} hover:underline`}>
                    Home
                  </Link>
                </li>
                <li className={colorClasses.text}>→</li>
                <li>
                  <Link 
                    to={`/series/${series.slug}`} 
                    className={`${colorClasses.accent} hover:underline`}
                  >
                    {series.title}
                  </Link>
                </li>
                <li className={colorClasses.text}>→</li>
                <li className={`${colorClasses.text} font-medium`}>
                  {manual.title}
                </li>
              </ol>
            </nav>

            {/* Manual Info */}
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h1 className={`text-2xl sm:text-3xl font-bold ${colorClasses.text}`}>
                    {manual.title}
                  </h1>
                  {isFree && (
                    <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full uppercase">
                      FREE
                    </span>
                  )}
                </div>
                <p className={`text-lg ${colorClasses.text} mb-4`}>
                  {manual.summary}
                </p>
                
                <div className="flex items-center space-x-6 text-sm">
                  <div className={`flex items-center ${colorClasses.text}`}>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {manual.duration}
                  </div>
                  
                  {manual.difficulty && (
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      manual.difficulty === 'beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      manual.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                      'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {manual.difficulty}
                    </div>
                  )}
                  
                  {progress > 0 && (
                    <div className={`${colorClasses.text}`}>
                      {progress}% complete
                    </div>
                  )}
                </div>
              </div>
              
              <div className="w-full sm:w-auto sm:ml-8 flex items-center gap-3 mt-4 sm:mt-0">
                {hasPython && (
                  <button
                    onClick={() => setShowGuide(true)}
                    className="px-3 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow"
                  >
                    Python Guide
                  </button>
                )}
                {!isFree && (
                  <div className="text-right">
                    <div className="text-sm font-bold text-gold-600 dark:text-gold-400 uppercase tracking-wide">
                      Premium access
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Progress Bar */}
      {progress > 0 && (
        <div className="bg-gray-200 dark:bg-gray-700 h-1">
          <div 
            className="bg-primary-600 h-1 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Content */}
      <main className="max-w-4xl mx-auto container-padding py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {mdxContent ? (
            <MDXWrapper>
              {React.createElement(mdxContent)}
            </MDXWrapper>
          ) : (
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <h1>Loading Manual Content...</h1>
              <p>Please wait while we load the manual content.</p>
            </div>
          )}
        </motion.div>
      </main>

      {/* Tags */}
      {manual.tags && manual.tags.length > 0 && (
        <section className="border-t border-gray-200 dark:border-gray-700 py-8">
          <div className="max-w-4xl mx-auto container-padding">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Topics Covered
            </h3>
            <div className="flex flex-wrap gap-2">
              {manual.tags.map((tag) => (
                <span 
                  key={tag}
                  className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Navigation */}
      <section className="border-t border-gray-200 dark:border-gray-700 py-8">
        <div className="max-w-4xl mx-auto container-padding">
          <div className="flex justify-between items-center">
            <Link 
              to={`/series/${series.slug}`}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
            >
              ← Back to {series.title}
            </Link>
            
            <div className="text-right">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                More from this series
              </div>
              <Link 
                to={`/series/${series.slug}`}
                className="btn-secondary text-sm"
              >
                View All Manuals
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {hasPython && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowGuide(true)}
            className="px-4 py-2 rounded-full bg-green-600 hover:bg-green-700 text-white text-sm font-semibold shadow-lg"
            aria-label="Open Python Guide"
          >
            Python Guide
          </button>
        </div>
      )}
      {hasPython && (
        <PythonGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} moduleTitle={manual.title} />
      )}
    </div>
  )
}
